import { Injectable, effect, signal } from '@angular/core';

export type YlTheme = 'light' | 'dark';
export type YlPalette = 'admin';

const STORAGE_KEY = 'adminV3Theme';

/**
 * Drives the `data-theme`/`data-palette` attributes the glassmorphism design
 * system reads (src/styles/design-system/_themes.scss). Scoped to admin-v3 —
 * see docs/1.17.8.26-glassmorphism-design-system-admin-v3-foundation.md item 3.
 *
 * Palette is fixed to `admin` — the admin surface has no palette picker
 * (Token Spec §1/§4). Only `theme` is user-controllable and persisted.
 */
@Injectable({
    providedIn: 'root',
})
export class ThemeService {
    private readonly _theme = signal<YlTheme>(this.loadInitialTheme());
    private readonly _palette = signal<YlPalette>('admin');

    readonly theme = this._theme.asReadonly();
    readonly palette = this._palette.asReadonly();

    constructor() {
        effect(() => {
            this.persistTheme(this._theme());
        });
    }

    toggleTheme(): void {
        this._theme.update(current => (current === 'light' ? 'dark' : 'light'));
    }

    setTheme(theme: YlTheme): void {
        this._theme.set(theme);
    }

    private loadInitialTheme(): YlTheme {
        try {
            return localStorage.getItem(STORAGE_KEY) === 'dark' ? 'dark' : 'light';
        } catch (error) {
            console.error('Failed to read persisted admin-v3 theme:', error);
            return 'light';
        }
    }

    private persistTheme(theme: YlTheme): void {
        try {
            localStorage.setItem(STORAGE_KEY, theme);
        } catch (error) {
            console.error('Failed to persist admin-v3 theme:', error);
        }
    }
}
